import { ContractModel } from 'shared/domain/contract/types/model';
import { DomainMessagesTypes } from 'shared/domain/messages/message';
import { ContractInDto } from 'shared/dtos/in/contract';
import { contractEntityToModel, contractInDtoToEntity } from 'serviceWorker/repository/contract/mapping';
import { storeAndReadServerAnswer } from 'serviceWorker/broadcasts/storeServerAnswer';
import { getUsersSynchronized } from 'serviceWorker/services/users/getUsers';
import { chooseErrorMessage } from 'shared/utils/errorHandling';
import * as dbCompanies from '../../db/companies/companies';
import * as dbContracts from '../../db/contracts/contracts';
import { broadcastUploadedContractError } from './error';
import { contractChannelContract } from 'shared/domain/contract/contractChannelContract';
import { makeEventPublisher } from 'serviceWorker/broadcasts/publishers';
import { scopeOfProject } from 'serviceWorker/helpers/dataScope';

export const broadcastUploadedContract = makeEventPublisher(contractChannelContract, DomainMessagesTypes.uploadedEntity);

export const broadcastDeletedContract = makeEventPublisher(contractChannelContract, DomainMessagesTypes.deletedEntity);

export const broadcastUpdatedContract = makeEventPublisher(contractChannelContract, DomainMessagesTypes.updatedEntity);

const broadcastCreatedContractModel = makeEventPublisher(contractChannelContract, DomainMessagesTypes.createdEntity);

/** The stored contract with the labels of its parties. */
async function readStoredContractModel(contractId: string): Promise<ContractModel | undefined> {
  const contractEntity = await dbContracts.getOne(contractId);
  if (!contractEntity) {
    return undefined;
  }
  const [users, companies] = await Promise.all([getUsersSynchronized(), dbCompanies.get()]);
  return contractEntityToModel(contractEntity, companies, users);
}

/**
 * Stores the created contract and answers its create with the model. IndexedDB
 * failures never reject it (see `storeAndReadServerAnswer`): the answer is then
 * the server's contract, without the parties' labels until the next sync.
 */
export const broadcastCreatedContract = async (
  contractInDto: ContractInDto,
  uniqueId: string,
  projectId?: string
): Promise<void> => {
  try {
    const { model } = await storeAndReadServerAnswer('contract', {
      store: () => scopeOfProject(projectId).write(() => dbContracts.addBatch([contractInDto])),
      readStored: () => readStoredContractModel(contractInDto._id),
      fromServerAnswer: () => contractEntityToModel(contractInDtoToEntity(contractInDto), [], []),
    });
    broadcastCreatedContractModel(model, uniqueId);
  } catch (error) {
    broadcastUploadedContractError(error, chooseErrorMessage(error), uniqueId);
  }
};
